import Vue from 'vue';
import { emitAppFloatButton, emitFloatButton, emitOpenArticle, emitOpenMyRequest, EventBus } from '@/common/eventBus';
import { BridgeService } from '../types';
import { Conversation } from '@/views/Chatroom/active-conversations/type';
import { LoginResult } from '@/common/types';
import { PleasedArticleAction } from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import { store } from '@/store';
import { platform, listenFromParent } from '../common';
import * as CommsService from './comms.service';
import { CommsCommand } from './comms.enum';

const BridgeEventsBus = new Vue();

export class ModernBridge implements BridgeService {
  isLegacyBridge = false;
  isMinimized = false;
  platform = platform;

  userRedirected = false;
  eventsStack: any[] = [];

  listenFromParent<T>(eventType: string, callback: (data: T) => void): () => void {
    return listenFromParent<T>(eventType, callback);
  }

  async login(data: { username: string; password: string }) {
    return await CommsService.getResult(CommsCommand.execute.login, data).then(
      (result) => result as unknown as LoginResult
    );
  }

  /**
   * Web/WAP only. Ask the host (Sportybet/F-com page) to generate a JWE token
   * via its backend callback and return it to the iframe.
   */
  async getJweToken() {
    return await CommsService.getResult(CommsCommand.execute.token).then((result) => result as unknown as string);
  }

  /**
   * Web/WAP only. Proxy Sportybet API calls through the host page.
   */
  callParentApi(params: {
    url: string;
    method: string;
    json: Record<string, unknown>;
    headers?: Record<string, string>;
  }) {
    return new Promise((resolve, reject) => {});
  }

  sendToParent(eventType: string, data?: unknown) {
    if (eventType === 'CLOSE') {
      this.minimize();
    }
  }

  minimize({ avoidNoop = false, inCall = false }: { avoidNoop?: boolean; inCall?: boolean } = {}) {
    const { platform, isMinimized } = this;

    // We want to redirect user to last event page when user minimize the chat & opens later
    this.userRedirected = false;

    if (isMinimized && avoidNoop) return;
    if (platform === 'wap' || platform === 'web') {
      emitFloatButton(true);
      store.commit('chat/updateNewMessage', false);
    } else {
      emitAppFloatButton(true);
    }

    CommsService.post(CommsCommand.bot.setOpen, false);
  }

  maximize({ avoidNoop = false }: { avoidNoop?: boolean } = {}) {
    const { platform, isMinimized } = this;
    if (!isMinimized && avoidNoop) return;
    if (platform === 'wap' || platform === 'web') {
      emitFloatButton(false);
    } else {
      emitAppFloatButton(false);
    }

    CommsService.post(CommsCommand.bot.setOpen, true);
  }

  toggleCover() {
    return this.isMinimized ? this.maximize() : this.minimize();
  }

  goToAction(action?: PleasedArticleAction | null) {}

  openActiveConversation(ticket: Conversation) {
    BridgeEventsBus.$emit('OPEN_ACTIVE_CONVERSATION', ticket);
  }

  onOpenActiveConversation(handler: (ticket: Conversation) => void) {
    BridgeEventsBus.$on('OPEN_ACTIVE_CONVERSATION', handler);
    return () => BridgeEventsBus.$off('OPEN_ACTIVE_CONVERSATION', handler);
  }

  redirectToLastEventPage() {
    if (!this.eventsStack.length) return;

    const lastEvent = this.eventsStack[this.eventsStack.length - 1];

    if (!lastEvent || this.userRedirected) return;

    this.userRedirected = true;

    if (lastEvent.myRequest) {
      emitOpenMyRequest(true);
    } else if (lastEvent.articleId) {
      emitOpenArticle({ isOpen: true, articleId: lastEvent.articleId, fetchArticle: true });
    }
  }
}
